import { createTheme, type Theme, type ThemeOptions } from '@mui/material/styles';

/**
 * Command-centre design system. The signature device is the severity/risk
 * colour scale — the one thing an operator's eye tracks first — kept consistent
 * everywhere (badges, risk distribution, incident rows). Everything else stays
 * quiet: graphite surfaces, a single azure signal accent, Inter for UI and
 * JetBrains Mono for the IDs, fingerprints, JSON and latency that fill this
 * domain.
 */

export type RiskKey = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

declare module '@mui/material/styles' {
  interface Palette {
    risk: Record<RiskKey, string>;
    surface: { raised: string; sunken: string };
  }
  interface PaletteOptions {
    risk?: Record<RiskKey, string>;
    surface?: { raised: string; sunken: string };
  }
}

const RISK_DARK: Record<RiskKey, string> = {
  LOW: '#3FB950',
  MEDIUM: '#D29922',
  HIGH: '#F85149',
  CRITICAL: '#DA3633',
};
const RISK_LIGHT: Record<RiskKey, string> = {
  LOW: '#1A7F37',
  MEDIUM: '#9A6700',
  HIGH: '#CF222E',
  CRITICAL: '#A40E26',
};

const SANS = '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif';
export const MONO = '"JetBrains Mono", ui-monospace, "SFMono-Regular", Menlo, monospace';

function base(mode: 'light' | 'dark'): ThemeOptions {
  const dark = mode === 'dark';
  return {
    palette: {
      mode,
      primary: { main: dark ? '#4C8DFF' : '#2563EB' },
      secondary: { main: dark ? '#8B5CF6' : '#7C3AED' },
      success: { main: dark ? '#3FB950' : '#1A7F37' },
      warning: { main: dark ? '#D29922' : '#9A6700' },
      error: { main: dark ? '#F85149' : '#CF222E' },
      info: { main: dark ? '#4C8DFF' : '#2563EB' },
      background: {
        default: dark ? '#0B0E14' : '#EEF1F6',
        paper: dark ? '#131823' : '#FFFFFF',
      },
      surface: dark
        ? { raised: '#1A2130', sunken: '#0E121B' }
        : { raised: '#F7F9FC', sunken: '#E9EDF3' },
      divider: dark ? '#263041' : '#E2E8F0',
      text: {
        primary: dark ? '#E6EAF2' : '#0F172A',
        secondary: dark ? '#97A2B6' : '#475569',
      },
      risk: dark ? RISK_DARK : RISK_LIGHT,
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: SANS,
      fontSize: 13,
      h1: { fontSize: '1.6rem', fontWeight: 650, letterSpacing: '-0.02em' },
      h2: { fontSize: '1.3rem', fontWeight: 650, letterSpacing: '-0.015em' },
      h3: { fontSize: '1.1rem', fontWeight: 600, letterSpacing: '-0.01em' },
      h4: { fontSize: '1rem', fontWeight: 600 },
      h5: { fontSize: '0.9rem', fontWeight: 600 },
      h6: { fontSize: '0.82rem', fontWeight: 600, letterSpacing: '0.02em' },
      subtitle2: { fontSize: '0.75rem', fontWeight: 600 },
      body2: { fontSize: '0.8125rem' },
      caption: { fontSize: '0.72rem', color: dark ? '#97A2B6' : '#475569' },
      button: { textTransform: 'none', fontWeight: 600 },
      overline: { fontWeight: 700, letterSpacing: '0.08em', fontSize: '0.66rem' },
    },
  };
}

function components(theme: Theme): ThemeOptions['components'] {
  const border = `1px solid ${theme.palette.divider}`;
  return {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': { '--mono': MONO },
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            transitionDuration: '0.01ms !important',
            scrollBehavior: 'auto !important',
          },
        },
        /*
         * ⚠️ **`.MuiOutlinedInput-root` is EXCLUDED, and that is a fix rather
         * than an omission.**
         *
         * An outlined field's label straddles the top border, and the fieldset
         * cuts a notch in that border for it. An `outline` is not a border and
         * nothing notches it — so this ring, drawn 2px outside the box, ran
         * straight through the label text of every outlined field in the
         * application whenever one was focused. Reported from a screenshot of a
         * focused select as "line over text"; invisible in every other
         * screenshot because it only appears while the field has focus.
         *
         * Nothing is lost by excluding them. MUI gives an outlined input its own
         * focus affordance — the notched outline thickens to 2px in the primary
         * colour — and that one IS notched, so it is both visible and correct.
         * Filled and standard inputs have no notch and keep the ring.
         */
        'a:focus-visible, button:focus-visible, [tabindex]:focus-visible, .MuiButtonBase-root:focus-visible, .MuiInputBase-root:not(.MuiOutlinedInput-root):focus-within':
          {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
        '::selection': { background: theme.palette.primary.main, color: '#fff' },
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-thumb': {
          background: theme.palette.divider,
          borderRadius: 8,
          border: `2px solid ${theme.palette.background.default}`,
        },
        body: { backgroundColor: theme.palette.background.default },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { backgroundImage: 'none' } },
    },
    MuiCard: { defaultProps: { elevation: 0 }, styleOverrides: { root: { border } } },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'default' },
      styleOverrides: {
        root: { borderBottom: border, backgroundColor: theme.palette.background.paper },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: { sizeSmall: { paddingInline: 10 } },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          /*
           * A comfortable touch target on a phone, and unchanged on a desktop.
           *
           * The icon buttons throughout the product sit around 30px, which is
           * fine for a mouse and below the 44px both platform guidelines ask
           * for. Widened only below `sm`, so the dense toolbars a mouse user
           * relies on are not loosened for everybody.
           */
          [theme.breakpoints.down('sm')]: { minWidth: 44, minHeight: 44 },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: border, paddingBlock: 7, paddingInline: 12, fontSize: '0.8125rem' },
        /*
         * A column heading reads as a TITLE, not as a caption.
         *
         * ⚠️ **`text.primary`, never an accent colour.** Blue is what a LINK is
         * in this product — a vehicle name, a registration, an incident — so
         * blue headings would read as a row of links across the top of every
         * table. Green, amber and red are the four risk levels. The only colour
         * left that can carry emphasis without claiming a meaning is the
         * ordinary text colour, which is also the darkest one available.
         *
         * ⚠️ **Which column is SORTED is signalled by the ARROW, not by being
         * darker.** MUI keeps an inactive `TableSortLabel` at `inherit` and an
         * active one at `text.primary`, so raising every heading to primary
         * removes a contrast that used to mark the sorted column. That is the
         * right trade here and only because the arrow already exists and is
         * shape rather than colour — the rule this product follows everywhere
         * else, where a risk word sits beside its ring.
         */
        head: {
          fontWeight: 700,
          color: theme.palette.text.primary,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          fontSize: '0.68rem',
          backgroundColor: theme.palette.surface.raised,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { '&:last-of-type td': { borderBottom: 0 }, transition: 'background-color .12s' },
        hover: { cursor: 'pointer' },
      },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600, fontSize: '0.7rem' }, sizeSmall: { height: 20 } },
    },
    MuiTooltip: { styleOverrides: { tooltip: { fontSize: '0.72rem' } } },
    MuiListItemButton: { styleOverrides: { root: { borderRadius: 6, marginInline: 8 } } },
    MuiDivider: { styleOverrides: { root: { borderColor: theme.palette.divider } } },
    MuiOutlinedInput: { styleOverrides: { input: { paddingBlock: 8.5 } } },
  };
}

/**
 * Tenant overrides the theme will accept.
 *
 * A deliberately narrow list. Branding may change what the product LOOKS like; it
 * may not change what anything MEANS — see the risk note below.
 */
export interface ThemeBranding {
  accent?: string | null;
  secondaryAccent?: string | null;
  fontFamily?: string | null;
  cornerRadius?: number | null;
  density?: 'COMFORTABLE' | 'COMPACT' | null;
  navStyle?: 'PAPER' | 'ACCENT' | null;
}

/** Spacing base. MUI's default is 8; compact tightens the whole rhythm at once. */
const DENSITY_SPACING: Record<'COMFORTABLE' | 'COMPACT', number> = {
  COMFORTABLE: 8,
  COMPACT: 6,
};

export function createAppTheme(mode: 'light' | 'dark', branding?: ThemeBranding): Theme {
  const options = base(mode);

  if (branding?.accent) {
    options.palette = { ...options.palette, primary: { main: branding.accent } };
  }
  if (branding?.secondaryAccent) {
    options.palette = { ...options.palette, secondary: { main: branding.secondaryAccent } };
  }
  if (branding?.fontFamily) {
    // Prepended, not replaced: the platform stack stays as the fallback chain so a
    // font that fails to load degrades to something readable rather than to serif.
    options.typography = {
      ...options.typography,
      fontFamily: `${branding.fontFamily}, ${SANS}`,
    };
  }
  if (branding?.cornerRadius != null) {
    options.shape = { borderRadius: branding.cornerRadius };
  }
  if (branding?.density) {
    /*
     * One spacing factor rather than a table of per-component overrides.
     *
     * Everything in the system is laid out in theme spacing units, so changing
     * the base tightens the whole rhythm consistently — where a list of component
     * paddings would drift the moment somebody added a component.
     */
    options.spacing = DENSITY_SPACING[branding.density];
  }

  /*
   * `palette.risk` is NEVER taken from branding, and this is the one rule in this
   * file worth defending.
   *
   * master-ui-specification.md §33: risk and feedback colour MEANINGS are not
   * remappable. A tenant may tune their accent freely, but "critical" rendered in
   * a calm green would make an alert look less serious than it is — a safety
   * problem rather than a cosmetic one. The accent is decorative; the risk scale
   * is semantic, and the two are kept apart here by simply not offering the hook.
   */

  const theme = createTheme(options);
  return createTheme(theme, { components: components(theme) });
}
